import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { Chart, registerables } from "chart.js";
import { OverlayTrigger, Popover } from 'react-bootstrap';
import axios from "axios";
import { API_BASE_URL } from './config';
import "./style.css";
import "./FrequencyResponseFunction.css";
import DampingRatioCell from "./DampingRatioCell";
import zoomPlugin from 'chartjs-plugin-zoom';
Chart.register(...registerables, zoomPlugin);

const CastingSFRF = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lineChartRef = useRef(null);
  const TempData = location.state?.TempData || {};

  const onCastingManagement = (e) => {
    e.preventDefault();
    navigate('/casting-management');
  };

  useEffect(() => {
    const axiosfetchData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/getdata/castingData`);
        console.log('CastingManagement', response.data);

        const uniqueCompaniesAndModels = new Set();
        const firstEntries = [];

        response.data.forEach(machine => {
          const key = `${machine.company}-${machine.machineModel}`;
          if (!uniqueCompaniesAndModels.has(key)) {
            uniqueCompaniesAndModels.add(key);
            firstEntries.push(machine);
          }
        });

        const matchingEntry = firstEntries.find(entry =>
          entry?.company === TempData.company && entry?.machineModel === TempData.machineModel
        );

        console.log('所有基準值', firstEntries);
        console.log('基準值', matchingEntry);

        let newLineLabels, newCoverData;
        if (matchingEntry) {
          ({ lineLabels: newLineLabels, coverData: newCoverData } = setDatas(TempData, matchingEntry));

          if (
            TempData.updata?.Amp_peak_freq_data?.length > 0 &&
            TempData.updata?.Amp_peak_data?.length > 0 &&
            Array.isArray(matchingEntry.updata?.Amp_peak_freq_data) &&
            Array.isArray(matchingEntry.updata?.Amp_peak_data)
          ) {
            const freqDiffs = TempData.updata.Amp_peak_freq_data.map((value, index) => {
              // 找到第一個基準頻率在±1內的index
              const baseIndex = matchingEntry.updata.Amp_peak_freq_data.findIndex(
                baseFreq => Math.abs(value - baseFreq) <= 1
              );
              if (baseIndex === -1) return null;
              const baseFreq = matchingEntry.updata.Amp_peak_freq_data[baseIndex];
              const diff = Math.abs((value - baseFreq) / baseFreq);
              return (diff * 100).toFixed(1);
            });

            const ampDiffs = TempData.updata.Amp_peak_data.map((value, index) => {
              const freqValue = TempData.updata.Amp_peak_freq_data[index];
              // 找到第一個基準頻率在±1內的index
              const baseIndex = matchingEntry.updata.Amp_peak_freq_data.findIndex(
                baseFreq => Math.abs(freqValue - baseFreq) <= 1
              );
              if (baseIndex === -1) return null;
              const baseAmp = matchingEntry.updata.Amp_peak_data[baseIndex];
              if (baseAmp === undefined) return null;
              const diff = Math.abs((value - baseAmp) / baseAmp);
              return (diff * 100).toFixed(1);
            });

            setFreqDifferences(freqDiffs);
            setAmpDifferences(ampDiffs);
          }
        } else {
          newLineLabels = TempData.updata.chartData.freq.slice(9);
          const colors1 = getRandomColor();
          newCoverData = [
            {
              label: `${TempData.updata.chartData.ch_name}`,
              data: TempData.updata.chartData.magn.slice(9),
              borderColor: colors1.borderColor,
              backgroundColor: colors1.backgroundColor,
            }
          ];
          setFreqDifferences(Array(TempData.updata.Amp_peak_freq_data.length).fill(0));
          setAmpDifferences(Array(TempData.updata.Amp_peak_data.length).fill(0));
        }

        let lineChart;
        if (lineChartRef.current) {
          if (lineChart) {
            lineChart.destroy();
          }
          console.log('我選擇的那一筆', TempData);
          lineChart = createLineChart(newLineLabels, newCoverData);
        }

        return () => {
          lineChart?.destroy();
        };
      } catch (error) {
        console.error("Error fetching latest data:", error);
      }
    };
    axiosfetchData();
  }, [TempData]);

  const setDatas = (Data, matchingEntry) => {
    const newLineLabels = Data.updata.chartData.freq.slice(9);
    const colors1 = getRandomColor();
    const colors2 = getRandomColor();

    const newCoverData = [
      {
        label: `${matchingEntry.updata.chartData.ch_name} (基準值)`,
        data: matchingEntry.updata.chartData.magn.slice(9),
        borderColor: colors2.borderColor,
        backgroundColor: colors2.backgroundColor,
      },
      {
        label: `${Data.updata.chartData.ch_name}`,
        data: Data.updata.chartData.magn.slice(9),
        borderColor: colors1.borderColor,
        backgroundColor: colors1.backgroundColor,
      }

    ];
    return { lineLabels: newLineLabels, coverData: newCoverData };
  };

  const createLineChart = (chartRef, datasets) => {
    const chartInstance = new Chart(lineChartRef.current, {
      type: "line",
      data: {
        labels: chartRef,
        datasets: datasets
      },
      options: {
        scales: {
          y: {
            ticks: { beginAtZero: true },
            title: { display: true, text: 'Amplitude (μm/N)', font: { size: 14 }, padding: 15 }
          },
          x: {
            type: 'linear',
            min: 1000, max: 5000,
            title: { display: true, text: 'Frequency (Hz)', font: { size: 14 }, padding: 15 },
            ticks: {
              beginAtZero: true,
              stepSize: 100,
              callback: function (value) {
                return Math.round(value);
              }
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        fill: true,
        elements: {
          point: { radius: 0, hitRadius: 10 },
          line: { tension: 0.4 }
        },
        plugins: {
          legend: {
            position: 'bottom',
            display: true,
            labels: {
              usePointStyle: true,
              pointStyle: 'rect',
              font: { size: 13 } // 折線圖圖例尺寸
            }
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label: function (context) {
                const label = context.dataset.label || '';
                const xValue = context.label;
                const yValue = context.raw.toFixed(4);
                return `${label}   ( ${xValue} , ${yValue} )`;
              },
              title: function () {
                return '';
              }
            }
          },
          datalabels: { display: false },
          zoom: {
            pan: {
              enabled: false,
            },
            zoom: {
              wheel: { enabled: false },
              pinch: { enabled: false },
              mode: 'x',
              drag: {
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                threshold: 2,
                delay: 50
              }
            },
            onZoom: ({ chart }) => {
              let xScale = chart.scales.x;
              let min = Math.max(xScale.min, 1000);
              let max = Math.min(xScale.max, 5000);

              chart.options.scales.x.min = min;
              chart.options.scales.x.max = max;
              chart.options.scales.x.ticks.stepSize = 100;
              chart.update();
            }
          }
        }
      }
    });

    lineChartRef.current.chartInstance = chartInstance;
    return chartInstance;
  };

  const resetZoom = () => {
    if (lineChartRef.current && lineChartRef.current.chartInstance) {
      lineChartRef.current.chartInstance.resetZoom();
    }
  };

  const [freqDifferences, setFreqDifferences] = useState([]);
  const [ampDifferences, setAmpDifferences] = useState([]);

  function getRandomColor() {
    const r = Math.floor(Math.random() * 255);
    const g = Math.floor(Math.random() * 255);
    const b = Math.floor(Math.random() * 255);
    return {
      borderColor: `rgb(${r}, ${g}, ${b})`,
      backgroundColor: `rgba(${r}, ${g}, ${b}, 0.2)`,
    };
  }

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        數值不在 0%-10% 的範圍內，為危險值！
      </Popover.Body>
    </Popover>
  );

  // 共用「幅差變紅」的判斷邏輯
  const isAmpNG = (row) => {
    if (!row?.matched) return false;

    const ampValue = parseFloat(row.amp);
    const ampDiffValue = parseFloat(row.ampDiff);

    if (Number.isNaN(ampValue) || Number.isNaN(ampDiffValue)) {
      return false;
    }

    return (
      (ampValue >= 0.05 && ampDiffValue > 10) ||
      (ampValue < 0.05 && ampDiffValue > 30)
    );
  };

  // 共用「線差(頻率)變紅」的判斷邏輯
  const isFreqNG = (row) => {
    if (!row?.matched) return false;

    const freqDiffValue = parseFloat(row.freqDiff);

    if (Number.isNaN(freqDiffValue)) {
      return false;
    }

    return (freqDiffValue < 0 || freqDiffValue > 3);
  };

  const validIndexesForTable = ((freqDifferences?.length > 0 ? freqDifferences : TempData.updata?.Amp_peak_freq_data) || [])
    .map((_, index) => index)
    .filter(index => freqDifferences?.[index] !== null && ampDifferences?.[index] !== null && freqDifferences?.[index] !== undefined);

  const tableData = validIndexesForTable.map(index => ({
    matched: true,
    freqDiff: freqDifferences?.[index],
    amp: TempData.updata?.Amp_peak_data?.[index],
    ampDiff: ampDifferences?.[index]
  }));

  // 同時依據「線差」與「幅差」作為 NG/G (瑕疵偵測) 的判斷基準
  const isNG = tableData.some((row) => isAmpNG(row) || isFreqNG(row));
  const hasData = tableData.length > 0;

  return (
    <div className="d-flex" style={{ width: '81.7%', margin: '0 auto' }}>
      <div className="container-fluid">
        <div style={{ height: '35px', width: '100%' }}></div>

        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#" onClick={onCastingManagement} style={{ color: '#B0B7C3', fontWeight: 'bold', fontSize: '15px' }}>鑄件資料庫</a>
            </li>
            <li
              className="breadcrumb-item"
              aria-current="page"
              style={{ color: '#5B82B1', fontWeight: 'bold', fontSize: '15px' }}>頻率響應函數 {TempData.machineModel}
            </li>
          </ol>
        </nav>

        {isNG && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginBottom: '10px' 
            }}>
            </div>
        )}

        <div className="tab-content" id="nav-tabContent">
          <div className="tab-pane show active p-0 my-4">

            {hasData && (
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <span
                  style={{
                    background: isNG ? '#d9534f' : '#5cb85c',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    padding: '6px 20px',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}
                >
                  {isNG ? 'NG / 有瑕疵' : 'OK / 合格'}
                </span>
              </div>
            )}

            <div className="d-flex justify-content-end gap-3">
              <button
                className="reset-btn bi bi-arrow-clockwise"
                onClick={resetZoom}
              >
              </button>
            </div>

            <div style={{ width: '100%', height: '500px' }}>
              <canvas ref={lineChartRef}></canvas>
            </div>

            <div className="table-margin">
              <div className="table-responsive">
                <table className="table table-striped table-bordered text-center frf-peak-table">
                  <colgroup>
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                  </colgroup>
                  <thead className="custom-thead ">
                    <tr>
                      <th>資料ID</th>
                      <th>頻率</th>
                      <th>振幅</th>
                      <th>阻尼比</th>
                      <th>線差(f)</th>
                      <th>幅差(a)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      (freqDifferences?.length > 0 ? freqDifferences : TempData.updata.Amp_peak_freq_data) || []
                    )
                      .map((_, index) => {
                        const freq = TempData.updata.Amp_peak_freq_data?.[index] || 'NaN';
                        const amp = freq === 'NaN' ? 'NaN' : (TempData.updata.Amp_peak_data?.[index] || 'NaN');
                        return (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td style={{ color: freq === 'NaN' ? '#CCCCCC' : '' }}>{freq}</td>
                            <td style={{ color: amp === 'NaN' ? '#CCCCCC' : '' }}>{amp}</td>
                            <DampingRatioCell value={TempData.updata.Damping_ratio_data?.[index]} />

                            <OverlayTrigger
                              trigger={['hover', 'focus']}
                              placement="top"
                              overlay={freqDifferences?.[index] < 0 || freqDifferences?.[index] > 3 ? popover : <></>}
                            >
                              <td
                                style={{ color: freq !== 'NaN' && (freqDifferences?.[index] < 0 || freqDifferences?.[index] > 3) ? '#EC4643' : (freq === 'NaN' ? '#CCCCCC' : ''), }}
                              >
                                {freq !== 'NaN' ? (isFinite(freqDifferences?.[index]) ? `${freqDifferences[index]}%` : '') : 'NaN'}
                              </td>
                            </OverlayTrigger>


                            <OverlayTrigger
                              trigger={['hover', 'focus']}
                              placement="top"
                              overlay={
                                (() => {
                                  if (amp !== 'NaN' && freq !== 'NaN') {
                                    const ampValue = parseFloat(amp);
                                    const freqDiff = ampDifferences?.[index];
                                    if ((ampValue >= 0.05 && freqDiff > 10) || (ampValue < 0.05 && freqDiff > 30)) {
                                      return popover;
                                    }
                                  }
                                  return <></>;
                                })()
                              }
                            >
                              <td
                                style={{
                                  color: (() => {
                                    if (amp !== 'NaN' && freq !== 'NaN') {
                                      const ampValue = parseFloat(amp);
                                      const freqDiff = ampDifferences?.[index];
                                      if ((ampValue >= 0.05 && freqDiff > 10) || (ampValue < 0.05 && freqDiff > 30)) {
                                        return '#EC4643';
                                      }
                                    }
                                    return '';
                                  })()
                                }}
                              >
                                {freq !== 'NaN' ? (isFinite(ampDifferences?.[index]) ? `${ampDifferences[index]}%` : '') : 'NaN'}
                              </td>
                            </OverlayTrigger>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default CastingSFRF;
